import React from "react";
import Badge from "../components/Badge";
import { Link } from "react-router-dom";
import { getProgress, hasBadge, awardBadge } from '../lib/progress';
import { hasActiveSubscription, registerUserRefreshCallback } from '../utils/userUtils';
import InteractiveMascot from '../components/InteractiveMascot';
import { getApiBaseUrl } from '../utils/apiConfig';

const CLASS_4_BADGES = [
  { 
    title: "First Steps", 
    subtitle: "Complete your first quiz", 
    icon: "🌟", 
    level: "bronze" 
  },
  { 
    title: "High Scorer", 
    subtitle: "Achieve 80%+ average score", 
    icon: "⭐", 
    level: "silver" 
  },
  { 
    title: "Perfect Score", 
    subtitle: "Get 100% on any quiz", 
    icon: "💯", 
    level: "gold" 
  },
  { 
    title: "Quiz Master", 
    subtitle: "Complete 10+ quizzes", 
    icon: "🎓", 
    level: "gold" 
  },
  { 
    title: "Consistent Player", 
    subtitle: "Play 5+ games this week", 
    icon: "🔥", 
    level: "silver" 
  },
  { 
    title: "Math Champion", 
    subtitle: "Achieve 90%+ average score", 
    icon: "👑", 
    level: "platinum" 
  }
];

const CLASS_4_TOPICS = [
  { name: "Addition & Subtraction", icon: "➕", progress: 0, total: 2 },
  { name: "Multiplication", icon: "✖️", progress: 0, total: 2 },
  { name: "Division", icon: "➗", progress: 0, total: 2 },
  { name: "Fractions", icon: "🍕", progress: 0, total: 2 },
  { name: "Decimals", icon: "🔢", progress: 0, total: 2 },
  { name: "Measurement", icon: "📏", progress: 0, total: 2 },
  { name: "Geometry", icon: "📐", progress: 0, total: 4 },
  { name: "Money", icon: "💰", progress: 0, total: 2 },
  { name: "Patterns", icon: "🔄", progress: 0, total: 2 }
];

export default function Dashboard({user, setUser}) {
  // Early return if user data is not loaded yet
  if (!user) {
    console.log('Dashboard: No user data provided');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('Dashboard: Received user data:', user);

  const [progress, setProgress] = React.useState(getProgress());
  const [userStats, setUserStats] = React.useState(null);
  const [statsLoading, setStatsLoading] = React.useState(true);
  const [currentUser, setCurrentUser] = React.useState(user);
  const [showRefreshToast, setShowRefreshToast] = React.useState(false);
  const [mascotInteractions, setMascotInteractions] = React.useState(0);

  // Listen for user data updates
  React.useEffect(() => {
    // Register for global user refresh
    const unregister = registerUserRefreshCallback((updatedUser) => {
      console.log('🔄 Dashboard: Global user refresh received:', updatedUser);
      setCurrentUser(updatedUser);
      if (setUser) setUser(updatedUser);
    });

    // Also listen for storage events (fallback)
    const handleUserUpdate = (e) => {
      if (e.key === 'user') {
        try {
          const updatedUser = JSON.parse(e.newValue || '{}');
          if (updatedUser.id && updatedUser.id !== -1) {
            console.log('Dashboard: User data updated from storage:', updatedUser);
            setCurrentUser(updatedUser);
            if (setUser) setUser(updatedUser);
          }
        } catch (error) {
          console.error('Error parsing updated user data:', error);
        }
      }
    };

    // Listen for custom user update events
    const handleCustomUserUpdate = (e) => {
      const updatedUser = e.detail?.user;
      if (updatedUser && updatedUser.id && updatedUser.id !== -1) {
        console.log('Dashboard: Custom user update received:', updatedUser);
        setCurrentUser(updatedUser);
        if (setUser) setUser(updatedUser);
      }
    };

    // Listen for refresh events
    const handleRefreshStarted = () => {
      setShowRefreshToast(true);
    };

    const handleRefreshCompleted = () => {
      setTimeout(() => setShowRefreshToast(false), 2000); // Hide after 2 seconds
    };

    window.addEventListener('storage', handleUserUpdate);
    window.addEventListener('userDataUpdated', handleCustomUserUpdate);
    window.addEventListener('userRefreshStarted', handleRefreshStarted);
    window.addEventListener('userRefreshCompleted', handleRefreshCompleted);
    
    return () => {
      unregister();
      window.removeEventListener('storage', handleUserUpdate);
      window.removeEventListener('userDataUpdated', handleCustomUserUpdate);
      window.removeEventListener('userRefreshStarted', handleRefreshStarted);
      window.removeEventListener('userRefreshCompleted', handleRefreshCompleted);
    };
  }, [setUser]);

  // Update current user when prop changes
  React.useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  // Fetch user stats from backend
  const fetchUserStats = async () => {
    if (!currentUser || currentUser.id === -1) {
      setStatsLoading(false);
      return;
    }

    try {
      setStatsLoading(true);
      console.log(`Fetching stats for user ID: ${currentUser.id}`);
      
      // Get token from localStorage
      const token = currentUser.token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch user's game statistics
      const response = await fetch(`${getApiBaseUrl()}/api/user/stats/${currentUser.id}`, { headers });
      console.log('Dashboard API Response status:', response.status);
      
      if (response.ok) {
        const stats = await response.json();
        console.log('Dashboard received stats from API:', stats);
        setUserStats(stats);
      } else {
        console.error('Dashboard API request failed with status:', response.status);
        const errorText = await response.text();
        console.error('Dashboard error response:', errorText);
        
        // Set empty stats instead of random data
        setUserStats({
          totalGamesPlayed: 0,
          averageScore: 0,
          bestScore: 0,
          totalCorrectAnswers: 0,
          totalQuestions: 0,
          favoriteSubject: 'No games played yet',
          weeklyProgress: 0,
          streak: 0
        });
      }
    } catch (error) {
      console.error('Dashboard error fetching user stats:', error);
      
      // Set empty stats instead of random data
      setUserStats({
        totalGamesPlayed: 0,
        averageScore: 0,
        bestScore: 0,
        totalCorrectAnswers: 0,
        totalQuestions: 0,
        favoriteSubject: 'No games played yet',
        weeklyProgress: 0,
        streak: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

  // Check and award badges based on real stats
  const checkAndAwardBadges = React.useCallback((stats) => {
    if (!stats) return;

    // First Steps - Complete your first quiz
    if (stats.totalGamesPlayed >= 1 && !hasBadge("First Steps")) {
      awardBadge("First Steps");
    }

    // High scorer - Score 80%+ average
    if (stats.averageScore >= 80 && !hasBadge("High Scorer")) {
      awardBadge("High Scorer");
    }

    // Math Champion - Score 90%+ average
    if (stats.averageScore >= 90 && !hasBadge("Math Champion")) {
      awardBadge("Math Champion");
    }

    // Perfect score - Get 100% on any quiz
    if (stats.bestScore >= 100 && !hasBadge("Perfect Score")) {
      awardBadge("Perfect Score");
    }

    // Quiz Master - Take 10+ quizzes
    if (stats.totalGamesPlayed >= 10 && !hasBadge("Quiz Master")) {
      awardBadge("Quiz Master");
    }

    // Consistent Player - Play 5+ games this week
    if (stats.weeklyProgress >= 5 && !hasBadge("Consistent Player")) {
      awardBadge("Consistent Player");
    }
  }, []);

  // Award badges when stats are loaded
  React.useEffect(() => {
    if (userStats && !statsLoading) {
      checkAndAwardBadges(userStats);
    }
  }, [userStats, statsLoading, checkAndAwardBadges]);

  // Fetch stats when user changes
  React.useEffect(() => {
    fetchUserStats();
  }, [currentUser]);

  // Update current user when prop changes
  React.useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'gamify:progress_v1') setProgress(getProgress());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const totalQuizzes = userStats?.totalGamesPlayed || 0;
  const avgScore = userStats?.averageScore || 0;
  const badgesEarned = CLASS_4_BADGES.filter(b => hasBadge(b.title)).length;
  const gamesPlayed = userStats?.totalGamesPlayed || 0;

  // Check subscription status using current user data
  const isGuest = !currentUser || currentUser.id === -1;
  
  // Check for active subscription (not cancelled, not expired)
  const hasActiveSub = currentUser && currentUser.subscription && 
    (currentUser.subscription.status === 'active' || currentUser.subscription.status === 1) &&
    currentUser.subscription.status !== 'cancelled' &&
    currentUser.subscription.status !== 3 &&
    (!currentUser.subscription.end_date || new Date() <= new Date(currentUser.subscription.end_date));
  
  // Check if subscription is cancelled
  const isSubscriptionCancelled = currentUser && currentUser.subscription &&
    (currentUser.subscription.status === 'cancelled' || currentUser.subscription.status === 3);
  
  // More robust role checking (handle both string and number)
  const userRole = parseInt(currentUser?.role) || 0;
  const isPremiumUser = currentUser && userRole === 2;
  const isAdmin = userRole === 3;
  
  // Access logic:
  // - Admin (role 3): Always has access
  // - Premium (role 2): Has access UNLESS subscription is cancelled
  // - Other roles (0, 1): Only has access with active subscription
  let isSubscribed = false;
  if (isAdmin) {
    isSubscribed = true; // Admin always has access
  } else if (isPremiumUser) {
    // Premium users are blocked if subscription is cancelled
    isSubscribed = !isSubscriptionCancelled && (hasActiveSub || !currentUser.subscription);
  } else {
    // Other roles need active subscription
    isSubscribed = hasActiveSub;
  }
  
  // TEMPORARY: Force unlock for testuser@example.com for debugging
  const isTestUser = currentUser?.email === 'testuser@example.com' || currentUser?.username === 'testuser@example.com';
  const finalIsSubscribed = isSubscribed || isTestUser;
  
  // Debug logging for subscription status
  console.log('Dashboard subscription check:', {
    user: currentUser,
    isGuest,
    userRole: currentUser?.role,
    userRoleParsed: userRole,
    userRoleType: typeof currentUser?.role,
    subscriptionStatus: currentUser?.subscription?.status,
    hasActiveSubscription: hasActiveSub,
    isPremiumUser,
    isAdmin,
    isSubscribed: isSubscribed,
    isTestUser,
    finalIsSubscribed,
    subscriptionObject: currentUser?.subscription,
    accessLogic: {
      isAdmin: isAdmin,
      hasBothRoleAndSubscription: isPremiumUser && hasActiveSub,
      finalAccess: isSubscribed
    }
  });

  // VISUAL DEBUG: Add alert to see if logic is working
  if (isTestUser) {
    console.log('🚨 TEST USER DETECTED - SHOULD UNLOCK GAMES!');
  }

  // Handle mascot interactions
  const handleMascotInteraction = (type, data) => {
    console.log('Mascot interaction:', type, data);
    setMascotInteractions(prev => prev + 1);
    
    // You could add achievements or rewards based on interactions
    if (mascotInteractions > 0 && mascotInteractions % 5 === 0) {
      console.log('🎉 Mascot interaction milestone reached!');
    }
  };
  
  // Show subscription prompt for locked features
  const LockedFeatureOverlay = ({ children, featureName }) => (
    <div className="relative">
      {children}
      <div className="absolute inset-0 bg-gray-900 bg-opacity-75 rounded-lg flex items-center justify-center">
        <div className="text-center text-white p-4">
          <div className="text-4xl mb-2">🔒</div>
          <h3 className="font-bold mb-2">{featureName} Locked</h3>
          <p className="text-sm mb-3">Subscribe to unlock this feature</p>
          <Link
            to="/subscription-manager"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Subscribe Now
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      {/* Refresh Toast Notification */}
      {showRefreshToast && (
        <div className="fixed top-4 right-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          <span className="text-sm font-medium">Updating your access...</span>
        </div>
      )}
      
      <div className="container mx-auto px-4">
        
        {/* DEBUG BANNER */}
        {isTestUser && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-4 text-center">
            🚨 DEBUG MODE: Test user detected | finalIsSubscribed = {finalIsSubscribed.toString()} | isSubscribed = {isSubscribed.toString()}
          </div>
        )}
        
        {/* Welcome Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/images/logo/MathVenture_logo.png" 
                alt="MathVenture Logo" 
                className="h-16 w-auto"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome back, {isGuest ? 'Guest' : ((currentUser.username || 'Student').split('@')[0].charAt(0).toUpperCase() + (currentUser.username || 'Student').split('@')[0].slice(1))}! 👋
                </h1>
                <p className="text-gray-600">
                  {isGuest ? 'Sign in to track your progress!' : 'Ready to master mathematics today?'}
                </p>
                {!finalIsSubscribed && !isGuest && (
                  <div className="mt-2 text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full inline-block">
                    🔒 Some features are locked - Subscribe to unlock all content
                  </div>
                )}
              </div>
            </div>
            <div className="text-6xl">
              🎓
            </div>
          </div>
        </div>

        {/* Guest User Login Prompt */}
        {isGuest && (
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Sign In to Track Your Progress! 🚀</h2>
                <p className="text-blue-100 mb-4">
                  Create an account to save your scores, earn badges, and compete on the leaderboard.
                </p>
                <div className="flex space-x-3">
                  <Link
                    to="/login"
                    className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-800 transition-colors"
                  >
                    Create Account
                  </Link>
                </div>
              </div>
              <div className="text-6xl">
                👤
              </div>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Quizzes Taken</span>
              <span className="text-2xl">📝</span>
            </div>
            <div className="text-3xl font-bold text-primary">
              {statsLoading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-12 rounded"></div>
              ) : (
                totalQuizzes
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Average Score</span>
              <span className="text-2xl">⭐</span>
            </div>
            <div className="text-3xl font-bold text-green-600">
              {statsLoading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
              ) : (
                `${avgScore}%`
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Badges Earned</span>
              <span className="text-2xl">🏆</span>
            </div>
            <div className="text-3xl font-bold text-yellow-600">
              {statsLoading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-8 rounded"></div>
              ) : (
                badgesEarned
              )}
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Games Played</span>
              <span className="text-2xl">🎮</span>
            </div>
            <div className="text-3xl font-bold text-purple-600">
              {statsLoading ? (
                <div className="animate-pulse bg-gray-200 h-8 w-12 rounded"></div>
              ) : (
                gamesPlayed
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Quick Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Class 4 Quiz - Always available */}
                <Link
                  to="/quiz/4"
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-3xl">📝</span>
                    <span className="text-sm opacity-75">Class 4</span>
                  </div>
                  <h3 className="text-xl font-bold mb-1">Class 4 Quiz</h3>
                  <p className="text-sm opacity-90">20 questions</p>
                  <div className="mt-4 flex items-center text-sm font-semibold">
                    <span>Start Now</span>
                    <span className="ml-2 group-hover:translate-x-2 transition-transform">→</span>
                  </div>
                </Link>

                {/* Class 5 Quiz - Premium feature */}
                {finalIsSubscribed ? (
                  <Link
                    to="/quiz/5"
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 hover:shadow-xl transition-all group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">📝</span>
                      <span className="text-sm opacity-75">Class 5</span>
                    </div>
                    <h3 className="text-xl font-bold mb-1">Class 5 Quiz</h3>
                    <p className="text-sm opacity-90">25 questions</p>
                    <div className="mt-4 flex items-center text-sm font-semibold">
                      <span>Start Now</span>
                      <span className="ml-2 group-hover:translate-x-2 transition-transform">→</span>
                    </div>
                  </Link>
                ) : (
                  <LockedFeatureOverlay featureName="Class 5 Quiz">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-3xl">📝</span>
                        <span className="text-sm opacity-75">Class 5</span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">Class 5 Quiz</h3>
                      <p className="text-sm opacity-90">25 questions</p>
                      <div className="mt-4 flex items-center text-sm font-semibold">
                        <span>Start Now</span>
                        <span className="ml-2">→</span>
                      </div>
                    </div>
                  </LockedFeatureOverlay>
                )}

                {/* Games - Premium feature */}
                {finalIsSubscribed ? (
                  <Link
                    to="/games"
                    className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6 hover:shadow-xl transition-all group"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">🎮</span>
                      <span className="text-sm opacity-75">Class 4 & 5</span>
                    </div>
                    <h3 className="text-xl font-bold mb-1">Play Games</h3>
                    <p className="text-sm opacity-90">Interactive learning games</p>
                    <div className="mt-4 flex items-center text-sm font-semibold">
                      <span>Explore Games</span>
                      <span className="ml-2 group-hover:translate-x-2 transition-transform">→</span>
                    </div>
                  </Link>
                ) : (
                  <LockedFeatureOverlay featureName="Premium Games">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-3xl">🎮</span>
                        <span className="text-sm opacity-75">Class 4 & 5</span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">Play Games</h3>
                      <p className="text-sm opacity-90">Interactive learning games</p>
                      <div className="mt-4 flex items-center text-sm font-semibold">
                        <span>Explore Games</span>
                        <span className="ml-2">→</span>
                      </div>
                    </div>
                  </LockedFeatureOverlay>
                )}

                {/* Leaderboard - Always available */}
                <Link
                  to="/leaderboard"
                  className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg p-6 hover:shadow-xl transition-all group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-3xl">🏆</span>
                    <span className="text-sm opacity-75">Top Performers</span>
                  </div>
                  <h3 className="text-xl font-bold mb-1">Leaderboard</h3>
                  <p className="text-sm opacity-90">View rankings</p>
                  <div className="mt-4 flex items-center text-sm font-semibold">
                    <span>View Rankings</span>
                    <span className="ml-2 group-hover:translate-x-2 transition-transform">→</span>
                  </div>
                </Link>
              </div>
            </div>

            {/* Class 4 Topics Progress */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Class 4 Topics</h2>
              <div className="space-y-3">
                {CLASS_4_TOPICS.map((topic, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{topic.icon}</span>
                      <span className="font-medium text-gray-900">{topic.name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="text-sm text-gray-600">
                        {topic.progress}/{topic.total}
                      </div>
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary transition-all"
                          style={{ width: `${(topic.progress / topic.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Badges */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Badges</h2>
              <div className="space-y-3">
                {CLASS_4_BADGES.map((badge, index) => (
                  <Badge
                    key={index}
                    title={badge.title}
                    subtitle={badge.subtitle}
                    icon={badge.icon}
                    level={badge.level}
                    earned={hasBadge(badge.title)}
                  />
                ))}
              </div>
            </div>

            {/* Motivational Card */}
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-2">Keep Going! 🚀</h3>
              <p className="text-sm opacity-90 mb-4">
                You're doing great! Complete more quizzes to unlock all badges and master Class 4 mathematics.
              </p>
              <div className="bg-white/20 rounded-lg p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span>Overall Progress</span>
                  <span className="font-bold">{Math.round((totalQuizzes / 10) * 100)}%</span>
                </div>
                <div className="w-full h-2 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all"
                    style={{ width: `${Math.min((totalQuizzes / 10) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Mascot */}
      <InteractiveMascot 
        emotion={finalIsSubscribed ? 'happy' : 'thinking'}
        position="bottom-right"
        variant="stickman"
        onInteraction={handleMascotInteraction}
      />
    </div>
  );
}